const ELM_NAME = 'expanding-drawer';
const CONTENT_SELECTOR = '[data-expanding-drawer-content]';
const ACTIVE_CLASS = 'expanding-drawer--active'

/**
 * Usage:
   <expanding-drawer
    data-config='{
      "name": "<Name of the drawer. This should also be the id of the section below>",
    }'
  >
    <section
      class="size-table w-full h-screen grid grid-rows-[max-content,_1fr] grid-cols-1 fixed right-0 top-0 z-fixed bg-white px-4 lg:px-6 overflow-hidden translate-x-full transition-transform duration-150 ease-out md:max-w-[484px]"
      id="size-table"
      data-expanding-drawer-content
      aria-expanded="false"
      aria-hidden="true"
      aria-live="polite"
    >
      <header class="size-table__header py-4 lg:py-6 mb-4 w-full left-0 bg-white">
        <nav class="order-2 absolute right-4 lg:right-6 -mt-[5px] lg:mt-0">
          <button
            type="button"
            class="size-table__close-button"
            data-expanding-drawer-close="size-table"
            aria-controls="size-table"
          >
            {%- render 'global-icon', icon: 'close', icon_class: 'w-7' -%}
          </button>
        </nav>
        <h2 class="size-table_heading uppercase text-sm leading-4 lg:text-lg lg:leading-6 font-bold">{{ "products.product.size_table" | t }}</h2>
        <hr class="absolute w-full bg-black-200 left-0 m-0 mt-4 lg:mt-6" />
      </header>

      {%- capture size_table_inner_content -%}
        {%- if size_table.title != blank -%}
          <h3 class="size-table__title mb-4 text-lg leading-6 font-bold">
            {{ size_table.title }}
          </h3>
        {%- endif -%}

        {%- if size_table.description -%}
          <div class="size-table__description rte text-xs leading-5 [&>div:first-child_*:only-child]:m-0">
            {{ size_table.description | metafield_tag }}
          </div>
        {%- endif -%}

        {%- capture media_grid -%}
          {%- for media_item in size_table.media.value -%}
            {% render 'format-image', image_object: media_item, class: 'w-full mt-4' %}
          {%- endfor -%}
        {%- endcapture -%}

        {%- if media_grid != blank -%}
          <div class="drawer__media flex flex-col lg:pt-6">
            {{ media_grid }}
          </div>
        {%- endif -%}
      {%- endcapture -%}

      {%- if size_table_inner_content != blank -%}
        <div class="size-table__inner-content overflow-auto pb-4">
          {{ size_table_inner_content }}
        </div>
      {%- endif -%}
    </section>
  </expanding-drawer>

  Add a button that triggers the drawer with the following attributes:
  <button
    type="button"
    aria-controls="<Name provided in data-config>"
    class="text-xs leading-5 underline"
    data-expanding-drawer-trigger="<Name provided in data-config>"
  >
    Open the drawer
  </button>
 */

class ExpandingDrawer extends HTMLElement {
  static get observedAttributes() {
    return ['data-config'];
  }

  /** @type {{ name: string }} */
  config = {};
  /** @type { Boolean } */
  isOpen = false;

  setConfig() {
    const config = JSON.parse(this.getAttribute('data-config') || '{}');
    const { name } = config;

    if (name) {
      this.config = {
        name,
      }

      return this.config;
    }
  }

  handleToggle(isOpen) {
    this.drawerContent.style.transform = isOpen ? 'translateX(0)' : '';

    this.drawerContent.setAttribute('aria-expanded', isOpen);
    this.drawerContent.setAttribute('aria-hidden', !isOpen);

    document.body.classList.toggle(ACTIVE_CLASS);
  }

  handleTriggerClick = (event) => {
    event.stopPropagation();
    this.isOpen = !this.isOpen;

    this.handleToggle(this.isOpen);
  }

  handleCloseClick = (event) => {
    event.stopPropagation();
    this.isOpen = false;

    this.handleToggle(this.isOpen);
  }

  handleClickOutside = (event) => {
    if (this.isOpen && !this.contains(event.target)) {
      event.preventDefault();
      this.handleCloseClick(event);
    }
  }

  attributeChangedCallback() {
    this.setConfig();
  }

  connectedCallback() {
    if (this.isConnected) {
      const config = this.setConfig();

      if (config) {
        this.drawerContent = this.querySelector(CONTENT_SELECTOR);
        this.triggers = document.querySelectorAll(`[data-expanding-drawer-trigger="${config.name}"]`);
        this.closeButton = this.querySelector(`[data-expanding-drawer-close="${config.name}"]`);

        this.triggers.forEach(trigger => {
          trigger.addEventListener('click', this.handleTriggerClick);
        });

        this.closeButton.addEventListener('click', this.handleCloseClick);

        document.addEventListener('click', this.handleClickOutside, { capture: true });
      }
    }
  }

  disconnectedCallback() {
    if (this.triggers?.length) {
      this.triggers.forEach(trigger => {
        trigger.removeEventListener('click', this.handleTriggerClick);
      });
    }

    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.handleCloseClick);
    }

    document.removeEventListener('click', this.handleClickOutside);
  }
}

if (!customElements.get(ELM_NAME)) {
  customElements.define(ELM_NAME, ExpandingDrawer);
}
